import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Center, Group, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchPublicResult } from '../api/publicEnrollment';
import PageShell from '../components/PageShell';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { resolveEnrollmentProduct } from '../utils/enrollmentProduct';

const DEFAULT_TITLE_SUCCESS = 'Enrollment Submitted Successfully';
const DEFAULT_MESSAGE_SUCCESS_WITH_CARD = 'The enrollment was submitted successfully. The copay card information is shown below.';
const DEFAULT_MESSAGE_SUCCESS = 'The enrollment was submitted successfully.';
const DEFAULT_TITLE = 'Enrollment Submitted';
const DEFAULT_MESSAGE = 'Your enrollment was received. Please contact support if you have questions.';

// tokenFingerprint is a short, non-reversible cache key for a bearer token (53-bit string hash).
function tokenFingerprint(token: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < token.length; i += 1) {
    const ch = token.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

interface PublicResultPageProps {
  product: string;
  publicToken: string;
}

export default function PublicResultPage({ product, publicToken }: PublicResultPageProps) {
  const { data: config, isLoading, error } = useResolvedProgram();
  const navigate = useNavigate();

  const resultQuery = useQuery({
    // Keyed by a fingerprint so the bearer token itself is not kept in the query cache key.
    queryKey: ['publicResult', product, tokenFingerprint(publicToken)],
    queryFn: () => fetchPublicResult(product, publicToken),
    enabled: Boolean(product && publicToken),
    retry: false,
  });

  const productConfig = resolveEnrollmentProduct(config, product);
  const effectiveConfig = productConfig?.effectiveConfig ?? config;

  const goToNewEnrollment = () => navigate(`/enrollments/${product}`, { replace: true });

  if (isLoading || resultQuery.isLoading) {
    return <Center h="100vh"><Loader size="lg" /></Center>;
  }
  if (error || !effectiveConfig || !productConfig) {
    return <Navigate to="/" replace />;
  }

  if (!publicToken || resultQuery.isError) {
    return (
      <PageShell config={effectiveConfig} support={effectiveConfig.support}>
        <meta name="referrer" content="no-referrer" />
        <Paper p="xl" radius="md" withBorder className="portal-panel">
          <Alert color="red" title="Result Not Found">
            This result link is invalid or has expired.
          </Alert>
        </Paper>

        <Button
          fullWidth
          onClick={goToNewEnrollment}
          className="portal-primary-button"
        >
          Submit Another Enrollment
        </Button>
      </PageShell>
    );
  }

  const result = resultQuery.data;
  const outcome = result?.outcome;
  const isSuccess = outcome === 'success';
  const outcomePageConfig = isSuccess
    ? productConfig?.pages.success
    : outcome === 'ineligible'
      ? productConfig?.pages.ineligible
      : outcome === 'rejected'
        ? productConfig?.pages.rejected
        : undefined;

  // Card details are shown only for enrolled outcomes (never for not-eligible enrollments).
  const cardDetails = isSuccess
    ? [
        { label: 'Card ID', value: result?.cardId },
        { label: 'BIN', value: result?.bin },
        { label: 'PCN', value: result?.pcn },
        { label: 'Group', value: result?.group },
      ].filter((detail): detail is { label: string; value: string } => Boolean(detail.value))
    : [];

  const defaultTitle = isSuccess ? DEFAULT_TITLE_SUCCESS : DEFAULT_TITLE;
  const defaultMessage = isSuccess
    ? (cardDetails.length > 0 ? DEFAULT_MESSAGE_SUCCESS_WITH_CARD : DEFAULT_MESSAGE_SUCCESS)
    : DEFAULT_MESSAGE;
  const title = result?.title ?? outcomePageConfig?.title ?? defaultTitle;
  const message = result?.message ?? outcomePageConfig?.message ?? defaultMessage;

  return (
    <PageShell
      config={effectiveConfig}
      support={outcomePageConfig?.showSupportInfo === true ? effectiveConfig.support : undefined}
    >
      <meta name="referrer" content="no-referrer" />
      <Paper p="xl" radius="md" withBorder className="portal-panel">
        <Stack gap="md">
          <Text fw={600} c="dimmed">Submission Status</Text>
          <Group gap="sm" align="center">
            <ThemeIcon color={isSuccess ? 'green' : 'yellow'} size="lg" radius="xl" variant="light">
              {isSuccess ? <IconCheck size={20} /> : <IconAlertCircle size={20} />}
            </ThemeIcon>
            <Title order={3}>{title}</Title>
          </Group>
          <Text>{message}</Text>

          {cardDetails.length > 0 && (
            <Paper
              p="md"
              radius="md"
              withBorder
              className="portal-panel portal-panel-muted"
              style={{ width: '100%', maxWidth: 360, alignSelf: 'center' }}
            >
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Text fw={700}>Copay Card Information</Text>
                  <ThemeIcon color="green" variant="light" radius="xl" size="lg">
                    <IconCheck size={18} />
                  </ThemeIcon>
                </Group>
                <Stack gap="xs">
                  {cardDetails.map((detail) => (
                    <Group key={detail.label} gap="sm" align="baseline" wrap="nowrap">
                      <Text size="sm" c="dimmed" ta="right" style={{ width: 72, flexShrink: 0 }}>
                        {detail.label}:
                      </Text>
                      <Text size="sm" fw={600} ta="left" style={{ flex: 1 }}>
                        {detail.value}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>

      <Button
        fullWidth
        onClick={goToNewEnrollment}
        className="portal-primary-button"
      >
        Submit Another Enrollment
      </Button>
    </PageShell>
  );
}
